import type { LangMap } from "./index";

/** Negozio ricompense XP, storico sblocchi e riepilogo settimanale. */
const base: LangMap = {
  it: {
    title: "Ricompense",
    subtitle: "Spendi gli XP guadagnati studiando",
    tabs: { shop: "Negozio", history: "Storico", weekly: "Settimana" },
    balance: "XP disponibili",
    spent: "XP spesi",
    total: "XP totali",
    redeem: "Riscatta",
    owned: "Sbloccato",
    notEnough: "XP insufficienti",
    redeemed: "Ricompensa sbloccata! 🎉",
    emptyHistory: "Non hai ancora riscattato nulla. Completa le sfide per accumulare XP.",
    items: {
      streak_freeze: { name: "Congela la serie", desc: "Protegge la tua streak per domani, anche se non studi." },
      weekend_pass: { name: "Pass weekend", desc: "Protegge sabato e domenica prossimi dalla perdita della serie." },
      extra_quest: { name: "Sfida extra", desc: "Sblocca oggi una sfida bonus da 30 minuti che vale 40 XP." },
      title_scholar: { name: "Titolo: Studioso", desc: "Un badge da mostrare sul tuo profilo pubblico." },
      title_owl: { name: "Titolo: Gufo notturno", desc: "Per chi studia anche a tarda sera." },
      title_legend: { name: "Titolo: Leggenda Lumen", desc: "Il badge più raro: solo per veri maratoneti dello studio." },
    },
    weekly: {
      title: "Riepilogo settimanale",
      range: "Dal {{from}} al {{to}}",
      minutes: "Minuti studiati",
      goal: "Obiettivo",
      activeDays: "Giorni attivi",
      protected: "Giorni di riposo protetti",
      xp: "XP della settimana",
      quests: "Sfide completate",
      quizAvg: "Media quiz",
      best: "Giorno migliore",
      streak: "Serie attuale",
      tips: "Consigli per la prossima settimana",
      noTips: "Studia qualche minuto e Lumen preparerà i consigli per te.",
    },
  },
  en: {
    title: "Rewards",
    subtitle: "Spend the XP you earned studying",
    tabs: { shop: "Shop", history: "History", weekly: "Week" },
    balance: "Available XP",
    spent: "XP spent",
    total: "Total XP",
    redeem: "Redeem",
    owned: "Unlocked",
    notEnough: "Not enough XP",
    redeemed: "Reward unlocked! 🎉",
    emptyHistory: "You haven't redeemed anything yet. Complete challenges to earn XP.",
    items: {
      streak_freeze: { name: "Streak freeze", desc: "Protects your streak tomorrow, even if you don't study." },
      weekend_pass: { name: "Weekend pass", desc: "Protects next Saturday and Sunday from breaking your streak." },
      extra_quest: { name: "Extra challenge", desc: "Unlocks a bonus 30-minute challenge today worth 40 XP." },
      title_scholar: { name: "Title: Scholar", desc: "A badge to show on your public profile." },
      title_owl: { name: "Title: Night owl", desc: "For those who study late at night." },
      title_legend: { name: "Title: Lumen legend", desc: "The rarest badge: for true study marathoners." },
    },
    weekly: {
      title: "Weekly recap",
      range: "From {{from}} to {{to}}",
      minutes: "Minutes studied",
      goal: "Goal",
      activeDays: "Active days",
      protected: "Protected rest days",
      xp: "XP this week",
      quests: "Challenges completed",
      quizAvg: "Quiz average",
      best: "Best day",
      streak: "Current streak",
      tips: "Tips for next week",
      noTips: "Study a few minutes and Lumen will prepare your tips.",
    },
  },
};

const EN = base["en"] as Record<string, any>;

const es: Record<string, unknown> = {
  ...EN,
  title: "Recompensas",
  subtitle: "Gasta los XP que has ganado estudiando",
  tabs: { shop: "Tienda", history: "Historial", weekly: "Semana" },
  balance: "XP disponibles",
  spent: "XP gastados",
  total: "XP totales",
  redeem: "Canjear",
  owned: "Desbloqueado",
  notEnough: "XP insuficientes",
  redeemed: "¡Recompensa desbloqueada! 🎉",
  emptyHistory: "Todavía no has canjeado nada. Completa retos para ganar XP.",
  weekly: { ...EN["weekly"], title: "Resumen semanal", range: "Del {{from}} al {{to}}", minutes: "Minutos estudiados", goal: "Objetivo", activeDays: "Días activos", protected: "Días de descanso protegidos", xp: "XP de la semana", quests: "Retos completados", quizAvg: "Media de cuestionarios", best: "Mejor día", streak: "Racha actual", tips: "Consejos para la próxima semana", noTips: "Estudia unos minutos y Lumen preparará tus consejos." },
};

const fr: Record<string, unknown> = {
  ...EN,
  title: "Récompenses",
  subtitle: "Dépense les XP gagnés en révisant",
  tabs: { shop: "Boutique", history: "Historique", weekly: "Semaine" },
  balance: "XP disponibles",
  spent: "XP dépensés",
  total: "XP au total",
  redeem: "Échanger",
  owned: "Débloqué",
  notEnough: "Pas assez d'XP",
  redeemed: "Récompense débloquée ! 🎉",
  emptyHistory: "Tu n'as encore rien échangé. Relève des défis pour gagner des XP.",
  weekly: { ...EN["weekly"], title: "Bilan de la semaine", range: "Du {{from}} au {{to}}", minutes: "Minutes révisées", goal: "Objectif", activeDays: "Jours actifs", protected: "Jours de repos protégés", xp: "XP de la semaine", quests: "Défis réussis", quizAvg: "Moyenne des quiz", best: "Meilleur jour", streak: "Série actuelle", tips: "Conseils pour la semaine prochaine", noTips: "Révise quelques minutes et Lumen préparera tes conseils." },
};

const de: Record<string, unknown> = {
  ...EN,
  title: "Belohnungen",
  subtitle: "Gib die XP aus, die du beim Lernen verdient hast",
  tabs: { shop: "Shop", history: "Verlauf", weekly: "Woche" },
  balance: "Verfügbare XP",
  spent: "Ausgegebene XP",
  total: "XP gesamt",
  redeem: "Einlösen",
  owned: "Freigeschaltet",
  notEnough: "Nicht genug XP",
  redeemed: "Belohnung freigeschaltet! 🎉",
  emptyHistory: "Du hast noch nichts eingelöst. Schließe Challenges ab, um XP zu sammeln.",
  weekly: { ...EN["weekly"], title: "Wochenrückblick", range: "Vom {{from}} bis {{to}}", minutes: "Gelernte Minuten", goal: "Ziel", activeDays: "Aktive Tage", protected: "Geschützte Ruhetage", xp: "XP dieser Woche", quests: "Abgeschlossene Challenges", quizAvg: "Quiz-Durchschnitt", best: "Bester Tag", streak: "Aktuelle Serie", tips: "Tipps für nächste Woche", noTips: "Lerne ein paar Minuten und Lumen bereitet deine Tipps vor." },
};

const pt: Record<string, unknown> = {
  ...EN,
  title: "Recompensas",
  subtitle: "Gasta os XP que ganhaste a estudar",
  tabs: { shop: "Loja", history: "Histórico", weekly: "Semana" },
  balance: "XP disponíveis",
  spent: "XP gastos",
  total: "XP totais",
  redeem: "Resgatar",
  owned: "Desbloqueado",
  notEnough: "XP insuficientes",
  redeemed: "Recompensa desbloqueada! 🎉",
  emptyHistory: "Ainda não resgataste nada. Completa desafios para ganhar XP.",
  weekly: { ...EN["weekly"], title: "Resumo semanal", range: "De {{from}} a {{to}}", minutes: "Minutos estudados", goal: "Objetivo", activeDays: "Dias ativos", protected: "Dias de descanso protegidos", xp: "XP da semana", quests: "Desafios concluídos", quizAvg: "Média dos quizzes", best: "Melhor dia", streak: "Sequência atual", tips: "Dicas para a próxima semana", noTips: "Estuda alguns minutos e o Lumen prepara as tuas dicas." },
};

export const rewards: LangMap = { ...base, es, fr, de, pt };
